import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api, { setAuthToken } from '../api/client';
import { useAuth } from '../components/AuthContext';
import toast from 'react-hot-toast';
import { Heart, Loader2, Eye, EyeOff } from 'lucide-react';
import { Turnstile } from '@marsidev/react-turnstile';

/**
 * CaregiverRegister — public page, reached via the invite link.
 * Validates the token, then lets the caregiver set a name + password.
 */
const CaregiverRegister = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const [inviteInfo, setInviteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: '', password: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);

  useEffect(() => {
    if (!token) { setError('No invite token found in the link.'); setLoading(false); return; }
    api.get(`/caregiver/invite-info/${token}`)
      .then(res => {
        const info = res.data.data;
        setInviteInfo(info);
        setForm(f => ({ ...f, name: info.caregiverName || '' }));
      })
      .catch(() => setError('This invite link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) { toast.error('Passwords do not match'); return; }
    if (form.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setSubmitting(true);
    try {
      const res = await api.post('/caregiver/register', { token, name: form.name, password: form.password, turnstileToken });
      if (res.data.token) {
        setAuthToken(res.data.token);
        setUser(res.data.user);
        toast.success(`Welcome, ${res.data.user.name}!`);
        navigate('/caregiver-dashboard', { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Registration failed');
      setTurnstileToken(null);
      turnstileRef.current?.reset();
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#f9f9fd]">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-[#f9f9fd] px-4">
      <div className="bg-white rounded-2xl shadow-sm border border-outline-variant/10 p-8 max-w-sm w-full text-center">
        <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Heart className="w-7 h-7 text-red-500" />
        </div>
        <h2 className="text-xl font-extrabold text-slate-900 mb-2">Invite not found</h2>
        <p className="text-sm text-slate-500">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f9f9fd] px-4">
      <div className="bg-white rounded-2xl shadow-sm border border-outline-variant/10 p-8 max-w-sm w-full">
        {/* Logo */}
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 bg-primary rounded-xl flex items-center justify-center">
            <Heart className="w-5 h-5 text-white" />
          </div>
          <span className="font-black text-slate-900 text-lg tracking-tight">SymptoConnect</span>
        </div>

        <h2 className="text-2xl font-extrabold text-slate-900 mb-1">Create your account</h2>
        <p className="text-sm text-slate-500 mb-6">
          <strong>{inviteInfo?.patientName}</strong> invited you as their{' '}
          {inviteInfo?.relationship !== 'other' ? inviteInfo?.relationship : 'caregiver'}.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">Your name</label>
            <input
              type="text" required value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">Email</label>
            <input
              type="email" value={inviteInfo?.caregiverEmail} disabled
              className="w-full px-3 py-2.5 text-sm bg-slate-100 border border-slate-200 rounded-xl text-slate-400 cursor-not-allowed"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">Password</label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'} required value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                className="w-full px-3 py-2.5 pr-10 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="Min. 8 characters"
              />
              <button type="button" onClick={() => setShowPw(p => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">Confirm password</label>
            <input
              type="password" required value={form.confirm}
              onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
              className="w-full px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>

          {/* Turnstile invisible bot protection */}
          <Turnstile
            ref={turnstileRef}
            siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
            onSuccess={setTurnstileToken}
            onExpire={() => setTurnstileToken(null)}
            onError={() => setTurnstileToken(null)}
            options={{ size: 'invisible' }}
          />

          <button type="submit" disabled={submitting || !turnstileToken}
            className="w-full py-3 bg-primary text-white text-sm font-bold rounded-xl hover:bg-primary/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Create account
          </button>
        </form>

        <p className="text-xs text-slate-400 text-center mt-4">
          You'll only see general health status — no clinical details.
        </p>
        <p className="text-xs text-slate-400 text-center mt-2">
          Already have an account?{' '}
          <Link to="/login" className="text-primary font-bold hover:underline">Log in</Link>
        </p>
      </div>
    </div>
  );
};

export default CaregiverRegister;
